import React, { useState, useRef, useEffect, useMemo } from "react";
import { flushSync } from "react-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";

import api from "../../services/api/axios";
import useModules from "../../hooks/useModules";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { ACTIONS_HEADER_BTN_SX } from "../../theme/buttonstyles";
import ReportPreview from "./ReportPreview";
import BackButton from "../../shared/components/BackButton";
import ArticleIcon from "@mui/icons-material/Article";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};


/* =====================================================
   AVAILABLE METRICS
===================================================== */

const AVAILABLE_METRICS = [
  { key: "inquiries", label: "Inquiries" },
  { key: "leads", label: "Leads - In Conversation With" },
  { key: "qualified_opportunities", label: "Qualified Opportunities" },
  { key: "rfis_responded_to", label: "RFIs Responded To" },
  { key: "prospect_closed_won", label: "Prospects (Closed Won)" },
  { key: "prospect_closed_lost", label: "Prospects (Closed Lost)" },
  { key: "prospects_added", label: "Prospects Added" },
  { key: "website_visitors", label: "Website Visitors" },
  { key: "bre_companies", label: "BR&E Companies Tracked" },
  { key: "jobs", label: "Jobs Created" },
  { key: "investment", label: "Investment" },
  { key: "projects_won", label: "New Projects" },
];

export default function ReportBuilder() {
  const previewRef = useRef(null);

  const { hasComponent } = useModules();
  const hasBre = hasComponent("bre.companies");
  const hasProspecting = hasComponent([
    "prospecting.companies",
    "prospecting.website_visits",
  ]);

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);
  const [pdfMode, setPdfMode] = useState(false);

  const [report, setReport] = useState({
    name: "",
    granularity: "month",
    date_mode: "custom",
    start: null,
    end: null,
    metrics: [],
    filters: {},
  });

  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const filteredMetrics = useMemo(() => {
    return AVAILABLE_METRICS.filter((m) => {
      if (m.key === "bre_companies" && !hasBre) return false;

      if (
        (m.key === "prospects_added" ||
          m.key === "leads" ||
          m.key === "qualified_opportunities" ||
          m.key === "rfis_responded_to" ||
          m.key === "prospect_closed_won" ||
          m.key === "prospect_closed_lost" ||
          m.key === "website_visitors") &&
        !hasProspecting
      )
        return false;

      return true;
    });
  }, [hasBre, hasProspecting]);

  useEffect(() => {
    setReport((prev) => ({
      ...prev,
      metrics: prev.metrics.filter((m) => {
        if (m === "bre_companies" && !hasBre) return false;

        if (
          (m === "prospects_added" ||
            m === "leads" ||
            m === "qualified_opportunities" ||
            m === "rfis_responded_to" ||
            m === "prospect_closed_won" ||
            m === "prospect_closed_lost" ||
            m === "website_visitors") &&
          !hasProspecting
        )
          return false;

        return true;
      }),
    }));
  }, [hasBre, hasProspecting]);

  const runPreview = async () => {
    setPreviewLoading(true);

    try {
      let start;
      let end;
      const today = new Date();

      if (report.date_mode === "ytd") {
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
      } else if (report.date_mode === "last_12_months") {
        start = new Date(today);
        start.setMonth(start.getMonth() - 12);
        end = today;
      } else {
        start = report.start;
        end = report.end;
      }

      const res = await api.post("/reports/run", {
        start: start?.toISOString().split("T")[0],
        end: end?.toISOString().split("T")[0],
        granularity: report.granularity,
        compare_to: "previous_period",
      });

      setPreviewData(res.data);
    } catch (err) {
      console.error("Preview failed", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const exportExcel = async (data) => {
    if (!data?.current?.totals) return;

    const XLSX = await import("xlsx");
    const totals = data.current.totals;
    const deltas = data.comparison?.delta || {};
    const timeSeries = data.current.time_series || [];

    const METRIC_LABELS = {
      inquiries: "Inquiries",
      prospects_added: "Prospects Added",
      bre_companies: "BR&E Companies Tracked",
      leads: "Leads - In Conversation With",
      qualified_opportunities: "Qualified Opportunities",
      rfis_responded_to: "RFIs Responded To",
      prospect_closed_won: "Prospects (Closed Won)",
      prospect_closed_lost: "Prospects (Closed Lost)",
      projects_won: "New Projects",
      investment: "Investment",
      jobs: "Jobs Created",
      website_visitors: "Website Visitors",
    };

    const summaryRows = Object.entries(METRIC_LABELS)
      .filter(([key]) => report.metrics.includes(key))
      .map(([key, label]) => ({
        Metric: label,
        Total: totals[key] ?? "",
        "Percent Change":
          deltas[`${key}_pct`] !== null && deltas[`${key}_pct`] !== undefined
            ? `${deltas[`${key}_pct`].toFixed(1)}%`
            : "",
      }));

    let breRunningTotal = 0;
    const timeSeriesRows = timeSeries.map((row) => {
      if (row.bre_companies != null) breRunningTotal += row.bre_companies;
      const transformed = {
        ...row,
        ...(report.metrics.includes("bre_companies")
          ? { bre_companies: breRunningTotal }
          : {}),
      };

      return Object.fromEntries(
        Object.entries({
          Period: transformed.period,
          ...Object.fromEntries(
            Object.entries(METRIC_LABELS)
              .filter(([key]) => report.metrics.includes(key))
              .map(([key, label]) => [label, transformed[key] ?? ""]),
          ),
        }),
      );
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(summaryRows),
      "Summary",
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(timeSeriesRows),
      "Time Series",
    );
    XLSX.writeFile(workbook, `${report.name || "report"}.xlsx`);
  };

  const exportPDF = async () => {
    if (!previewRef.current) return;

    flushSync(() => setPdfMode(true));

    const saved = [];
    previewRef.current.querySelectorAll("*").forEach((el) => {
      const cs = window.getComputedStyle(el);
      if (
        ["auto", "scroll"].includes(cs.overflowX) ||
        ["auto", "scroll"].includes(cs.overflowY)
      ) {
        saved.push({
          el,
          overflowX: el.style.overflowX,
          overflowY: el.style.overflowY,
          maxHeight: el.style.maxHeight,
        });
        el.style.overflowX = "visible";
        el.style.overflowY = "visible";
        el.style.maxHeight = "none";
      }
    });

    const sections = Array.from(
      previewRef.current.querySelectorAll("[data-pdf-section]"),
    );

    const pdf = new jsPDF("landscape", "pt", "a4");
    const PW = pdf.internal.pageSize.getWidth();
    const PH = pdf.internal.pageSize.getHeight();

    const HEADER_H = 54;
    const FOOTER_H = 24;
    const PAGE_PAD = 22;
    const GAP = 14;
    const COLS = 2;
    const CONTENT_TOP = HEADER_H + 16;
    const CONTENT_H = PH - CONTENT_TOP - FOOTER_H - 16;

    const SECTION_LABELS = {
      kpi: "Key Performance Indicators",
      charts: "Monthly Trends",
      table: "Monthly Breakdown",
    };

    const drawChrome = (sectionLabel, pageLabel) => {
      // Header background
      pdf.setFillColor(0, 51, 108);
      pdf.rect(0, 0, PW, HEADER_H, "F");

      // Green accent strip
      pdf.setFillColor(100, 131, 21);
      pdf.rect(0, HEADER_H, PW, 3, "F");

      // Report name — muted, top-left
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(150, 190, 225);
      pdf.text(report.name || "Performance Report", PAGE_PAD, 16);

      // Section title — large, white, bottom-left of header
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(255, 255, 255);
      pdf.text(sectionLabel, PAGE_PAD, 42);

      // Date — top-right
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(150, 190, 225);
      const dateStr = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      pdf.text(dateStr, PW - PAGE_PAD, 16, { align: "right" });

      // Page label — bottom-right of header
      if (pageLabel) {
        pdf.setFontSize(8.5);
        pdf.setTextColor(200, 220, 245);
        pdf.text(pageLabel, PW - PAGE_PAD, 42, { align: "right" });
      }

      // Footer divider
      pdf.setDrawColor(210, 218, 230);
      pdf.setLineWidth(0.6);
      pdf.line(PAGE_PAD, PH - FOOTER_H, PW - PAGE_PAD, PH - FOOTER_H);

      // Footer text
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(165, 178, 200);
      pdf.text("Business Engage", PAGE_PAD, PH - 8);
      pdf.text(
        new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
        PW - PAGE_PAD,
        PH - 8,
        { align: "right" },
      );

      pdf.setTextColor(0, 0, 0);
    };

    let firstPage = true;

    for (const section of sections) {
      const sectionKey = section.dataset.pdfSection;
      const sectionLabel = SECTION_LABELS[sectionKey] || sectionKey;

      if (sectionKey === "charts") {
        const chartCards = Array.from(section.querySelectorAll("[data-pdf-chart]"));
        if (!chartCards.length) continue;

        const canvases = [];
        for (const card of chartCards) {
          canvases.push(
            await html2canvas(card, { scale: 2, useCORS: true, backgroundColor: "#ffffff" }),
          );
        }

        const chartW = (PW - PAGE_PAD * 2 - GAP * (COLS - 1)) / COLS;
        const chartH = (canvases[0].height * chartW) / canvases[0].width;
        const rowsPerPage = Math.max(
          1,
          Math.floor((CONTENT_H + GAP) / (chartH + GAP)),
        );
        const perPage = COLS * rowsPerPage;
        const totalChartPages = Math.ceil(canvases.length / perPage);

        for (let b = 0; b < canvases.length; b += perPage) {
          if (!firstPage) pdf.addPage();
          firstPage = false;

          const chartPageNum = Math.floor(b / perPage) + 1;
          const pageLabel =
            totalChartPages > 1
              ? `Page ${chartPageNum} of ${totalChartPages}`
              : "";
          drawChrome(sectionLabel, pageLabel);

          canvases.slice(b, b + perPage).forEach((canvas, idx) => {
            const col = idx % COLS;
            const row = Math.floor(idx / COLS);
            pdf.addImage(
              canvas.toDataURL("image/png"),
              "PNG",
              PAGE_PAD + col * (chartW + GAP),
              CONTENT_TOP + row * (chartH + GAP),
              chartW,
              chartH,
            );
          });
        }
      } else {
        // For the table, expand the wrapper to its full content width so
        // html2canvas (which sizes the canvas from getBoundingClientRect) captures all columns
        const savedSectionWidth = section.style.width;
        if (sectionKey === "table") {
          section.style.width = "max-content";
        }

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        });

        if (sectionKey === "table") {
          section.style.width = savedSectionWidth;
        }

        if (!firstPage) pdf.addPage();
        firstPage = false;

        drawChrome(sectionLabel);

        const maxW = PW - PAGE_PAD * 2;
        let w = maxW;
        let h = (canvas.height * w) / canvas.width;

        if (h > CONTENT_H) {
          h = CONTENT_H;
          w = (canvas.width * h) / canvas.height;
        }

        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          PAGE_PAD + (maxW - w) / 2,
          CONTENT_TOP + (CONTENT_H - h) / 2,
          w,
          h,
        );
      }
    }

    saved.forEach(({ el, overflowX, overflowY, maxHeight }) => {
      el.style.overflowX = overflowX;
      el.style.overflowY = overflowY;
      el.style.maxHeight = maxHeight;
    });

    flushSync(() => setPdfMode(false));

    pdf.save(`${report.name || "report"}.pdf`);
  };

  const toggleMetric = (key) => {
    setReport((prev) => ({
      ...prev,
      metrics: prev.metrics.includes(key)
        ? prev.metrics.filter((k) => k !== key)
        : [...prev.metrics, key],
    }));
  };

  const allSelected = report.metrics.length === filteredMetrics.length;

  const isRunDisabled =
    report.metrics.length === 0 ||
    (report.date_mode === "custom" && (!report.start || !report.end));

  const DATE_PRESETS = [
    { key: "ytd", label: "Year to Date" },
    { key: "last_12_months", label: "Last 12 Months" },
    { key: "custom", label: "Custom" },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: PAGE_BG,
          pb: 4,
        }}
      >
        {/* ===== Sticky Header ===== */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            borderBottom: "1px solid #dce4ef",
            background: "#ffffff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: { xs: 2, md: 3 },
              py: 1.75,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <BackButton to="/metrics" />
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  background: BRAND_GREEN,
                  color: "#ffffff",
                  display: "grid",
                  placeItems: "center",
                  flex: "0 0 auto",
                }}
              >
                <ArticleIcon sx={{ fontSize: 19 }} />
              </Box>
              <Box
                sx={{
                  fontSize: 21,
                  fontWeight: 800,
                  color: BRAND_BLUE,
                  lineHeight: 1.05,
                  whiteSpace: "nowrap",
                }}
              >
                Report Builder
              </Box>
            </Box>

            <Box sx={{ ml: "auto", display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                disabled={!previewData}
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                startIcon={<FileDownloadIcon />}
                sx={ACTIONS_HEADER_BTN_SX}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportAnchorEl}
                open={exportOpen}
                onClose={() => setExportAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    exportExcel(previewData);
                    setExportAnchorEl(null);
                  }}
                >
                  <TableChartIcon sx={{ mr: 1.25, color: BRAND_GREEN }} />
                  Export Excel
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    exportPDF();
                    setExportAnchorEl(null);
                  }}
                >
                  <PictureAsPdfIcon sx={{ mr: 1.25, color: "#B42318" }} />
                  Export PDF
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>

        {/* ===== Two-panel layout ===== */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "380px 1fr" },
            gap: 3,
            px: { xs: 2, md: 3 },
            mt: 3,
            alignItems: "start",
          }}
        >
          {/* ── Left: Config panel ── */}
          <Card
            sx={{
              ...CARD_SX,
              height: { xs: "auto", lg: 740 },
              color: BRAND_BLUE,
              "& input::placeholder": {
                color: BRAND_BLUE,
                opacity: 0.65,
              },
              "& .MuiInputBase-input": {
                color: BRAND_BLUE,
              },
              "& .MuiInputLabel-root": {
                color: BRAND_BLUE,
              },
              "& .MuiSvgIcon-root": {
                color: BRAND_BLUE,
              },
            }}
          >
            {/* Panel header */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid #eef2f8",
                background: "linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: BRAND_BLUE,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Configuration
              </Typography>
            </Box>

            <CardContent
              sx={{
                p: 3,
                boxSizing: "border-box",
                height: { xs: "auto", lg: "calc(100% - 53px)" },
                overflowY: "auto",
              }}
            >
              {/* Step 1: Name */}
              <Box sx={{ mb: 3.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(0,51,108,0.1)",
                      color: BRAND_BLUE,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    1
                  </Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, color: BRAND_BLUE }}
                  >
                    Report Name
                  </Typography>
                </Box>
                <input
                  placeholder="e.g. Q3 Performance Summary"
                  value={report.name}
                  onChange={(e) =>
                    setReport((r) => ({ ...r, name: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: 14,
                    fontWeight: 500,
                    color: BRAND_BLUE,
                    border: "1.5px solid #dce4ef",
                    borderRadius: 8,
                    outline: "none",
                    background: "#fafbfd",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = BRAND_BLUE;
                    e.target.style.background = "#fff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#dce4ef";
                    e.target.style.background = "#fafbfd";
                  }}
                />
              </Box>

              {/* Step 2: Date Range */}
              <Box sx={{ mb: 3.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(0,51,108,0.1)",
                      color: BRAND_BLUE,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    2
                  </Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, color: BRAND_BLUE }}
                  >
                    Date Range
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mb: report.date_mode === "custom" ? 2 : 0,
                  }}
                >
                  {DATE_PRESETS.map(({ key, label }) => (
                    <Button
                      key={key}
                      onClick={() =>
                        setReport((r) => ({ ...r, date_mode: key }))
                      }
                      sx={{
                        minHeight: 32,
                        px: 1.75,
                        borderRadius: 999,
                        border: "1.5px solid",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12.5,
                        borderColor:
                          report.date_mode === key ? BRAND_BLUE : "#dce4ef",
                        color: BRAND_BLUE,
                        backgroundColor:
                          report.date_mode === key ? "rgba(0,51,108,0.1)" : "#f8fafc",
                        "&:hover": {
                          backgroundColor:
                            report.date_mode === key ? "rgba(0,51,108,0.16)" : "#eef2f8",
                          borderColor:
                            report.date_mode === key ? BRAND_BLUE : "#c8d5e8",
                        },
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
                {report.date_mode === "custom" && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    <DatePicker
                      label="Start Date"
                      value={report.start}
                      onChange={(val) =>
                        setReport((r) => ({ ...r, start: val }))
                      }
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={report.end}
                      onChange={(val) => setReport((r) => ({ ...r, end: val }))}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Step 3: Metrics */}
              <Box sx={{ mb: 3.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(0,51,108,0.1)",
                      color: BRAND_BLUE,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    3
                  </Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, color: BRAND_BLUE }}
                  >
                    Metrics
                  </Typography>
                  <Button
                    onClick={() =>
                      setReport((r) => ({
                        ...r,
                        metrics: allSelected
                          ? []
                          : filteredMetrics.map((m) => m.key),
                      }))
                    }
                    sx={{
                      ml: "auto",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "none",
                      color: BRAND_BLUE,
                      p: 0,
                      minWidth: 0,
                      "&:hover": {
                        background: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {allSelected ? "Deselect All" : "Select All"}
                  </Button>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {filteredMetrics.map((m) => {
                    const selected = report.metrics.includes(m.key);
                    return (
                      <Chip
                        key={m.key}
                        label={m.label}
                        onClick={() => toggleMetric(m.key)}
                        sx={{
                          height: 30,
                          fontSize: 12.5,
                          fontWeight: 700,
                          cursor: "pointer",
                          border: "1.5px solid",
                          borderColor: selected ? BRAND_BLUE : "#dce4ef",
                          backgroundColor: selected ? "rgba(0,51,108,0.1)" : "#f8fafc",
                          color: BRAND_BLUE,
                          "& .MuiChip-label": { px: 1.25 },
                          "&:hover": {
                            backgroundColor: selected ? "rgba(0,51,108,0.16)" : "#eef2f8",
                            borderColor: selected ? BRAND_BLUE : "#c8d5e8",
                          },
                          transition: "all 0.15s ease",
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Run button */}
              <Button
                fullWidth
                onClick={runPreview}
                disabled={isRunDisabled}
                startIcon={<PlayArrowIcon />}
                sx={{
                  minHeight: 44,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 14,
                  background: isRunDisabled ? "#f8fafc" : "#ffffff",
                  border: `1.5px solid ${BRAND_BLUE}`,
                  color: BRAND_BLUE,
                  boxShadow: isRunDisabled
                    ? "none"
                    : "0 4px 14px rgba(0,51,108,0.28)",
                  "&:hover": {
                    background: isRunDisabled ? "#f8fafc" : "rgba(0,51,108,0.08)",
                    boxShadow: isRunDisabled
                      ? "none"
                      : "0 6px 18px rgba(0,51,108,0.36)",
                  },
                  "&.Mui-disabled": {
                    color: BRAND_BLUE,
                    borderColor: BRAND_BLUE,
                    opacity: 0.55,
                  },
                }}
              >
                {previewLoading ? "Running…" : "Run Preview"}
              </Button>
            </CardContent>
          </Card>

          {/* ── Right: Preview panel ── */}
          {previewData ? (
            <Card
              ref={previewRef}
              elevation={0}
              sx={{ ...CARD_SX, border: "none", boxShadow: "none", background: "transparent", backgroundColor: "transparent" }}
            >
              <CardContent sx={{ p: 0 }}>
                <ReportPreview
                  data={previewData}
                  loading={previewLoading}
                  selectedMetrics={report.metrics}
                  showCarousel={false}
                  pdfMode={pdfMode}
                />
              </CardContent>
            </Card>
          ) : (
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 760,
                borderRadius: 2,
                gap: 2,
                color: "#8a98b3",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#eef2f8",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <BarChartIcon sx={{ fontSize: 32, color: "#b0bdd4" }} />
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#344863",
                    mb: 0.5,
                  }}
                >
                  No preview yet
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: "#8a98b3", maxWidth: 260 }}
                >
                  Configure your report on the left and click Run Preview to see
                  results here.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
